import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, headings, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { StyledTypography } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React from "react";
import styled, { css } from "styled-components";

const enum fields {
    columnsCount = "columnsCount",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.columnsCount]: number;
    };
}

// --- Styled Components for Layout and Styling ---

export interface FullWidthLinkListStyles {
    container?: GridContainerProps;
    columns?: GridItemProps;
}

export const fullWidthLinkListStyles: FullWidthLinkListStyles = {
    container: {
        gap: 16,
        offsetCss: css`
            justify-content: center;
            max-width: 848px;
            margin: 0 auto;
            
            @media only screen and (max-width: 992px) {
                justify-content: flex-start;
                row-gap: 32px;
            }
        `,
        css: css`
            ${GridItemStyle} {
                padding-top: 0;
                padding-bottom: 0;
            }
        `,
    },
    columns: {
        width: [6, 6, 3, 3, 3],
        css: css`
            .link-list-wrapper {
                &:not(:last-of-type) {
                    padding-bottom: 40px;
                }
            }
            .list-wrapper {
                padding: 0;
            }

            .links-wrapper {
                margin: 8px 0;
            }

            .list-title {
                font-size: ${headings.h4.fontSize};
                line-height: ${headings.h4.lineHeight};
                font-weight: ${fontWeights.semiBold};
                text-transform: capitalize;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: ${headings.h4.fontSizeSmall};
                                line-height: ${headings.h4.lineHeightSmall};
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }

            .list-link {
                font-size: ${fontSizes.small};
                line-height: ${lineHeights.defaultLarge};
                font-weight: ${fontWeights.medium};
                text-transform: capitalize;

                ${StyledTypography} {
                    color: ${themeColors.secondary.danaBrightBlue};
                    display: flex;
                    align-items: center;
                }

                &:hover {
                    ${StyledTypography} {
                        color: ${themeColors.primary.white};
                    }
                }
            }
        `,
    },
};

const FullWidthLinkListWrapper = styled.div`
    background-color: ${themeColors.primary.black};
    color: ${themeColors.primary.white};
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap; // Allows columns to wrap on smaller screens
    justify-content: center; // Center columns if less than 4

    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    padding: 40px 16px;
                `,
                css`
                    padding: 40px 16px;
                `,
                css`
                    padding: 64px 24px;
                `,
                css`
                    padding: 80px 40px;
                `,
                css`
                    padding: 80px 40px;
                `
            ],
            "min",
        )}
`;

const styles = fullWidthLinkListStyles;

const FullWidthLinkListWidgetComponent: React.FC<OwnProps> = ({ id, fields }) => {
    const numberOfColumns = fields.columnsCount > 0 ? fields.columnsCount : 0;

    const columnItems = Array.from({ length: numberOfColumns }, (_, index) => (
        <GridItem key={index} {...styles.columns}>
            <Zone contentId={id} zoneName={`fullWidthLinkListColumn${index + 1}`}></Zone>
        </GridItem>
    ));

    return (
        <FullWidthLinkListWrapper>
            <GridContainer {...styles.container}>{columnItems}</GridContainer>
        </FullWidthLinkListWrapper>
    );
};

// Re-export the widgetModule defined previously
const widgetModule: WidgetModule = {
    component: FullWidthLinkListWidgetComponent,
    definition: {
        group: "Common",
        displayName: "Full Width Link List",
        fieldDefinitions: [
            {
                name: fields.columnsCount,
                displayName: "No. of columns visible",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                min: 2,
                max: 4,
            },
        ],
    },
};

export default widgetModule;
